import React, { useEffect, useState, useCallback } from 'react';
import api, { formatApiError } from '../api';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n || 0);

const TABS = [
    { key: 'trial',    label: 'Trial Balance' },
    { key: 'pl',       label: 'Profit & Loss' },
    { key: 'bs',       label: 'Balance Sheet' },
    { key: 'vat',      label: 'VAT Return' },
    { key: 'journals', label: 'Journals' },
    { key: 'coa',      label: 'Chart of Accounts' },
];

export default function GeneralLedger() {
    const [tab, setTab] = useState('trial');
    const today = new Date().toISOString().slice(0, 10);
    const ytdStart = '2025-04-06';
    const [start, setStart] = useState(ytdStart);
    const [end, setEnd] = useState(today);

    return (
        <div data-testid="ledger-page" className="bm-fade-in">
            <PageHeader title="General Ledger"
                description="Double-entry bookkeeping. Posts automatically from invoices, payments and categorised Monzo expenses." />
            <div className="px-8 py-6 space-y-5">
                <div className="flex flex-wrap items-end gap-4 justify-between">
                    <div className="flex gap-1 border-b border-[color:var(--bm-border)] flex-wrap">
                        {TABS.map(t => (
                            <button key={t.key} onClick={() => setTab(t.key)}
                                className={`px-4 py-2.5 text-[13px] font-medium border-b-2 transition -mb-px ${tab === t.key ? 'border-[color:var(--bm-primary)] text-[color:var(--bm-primary)]' : 'border-transparent text-[color:var(--bm-text-muted)] hover:text-[color:var(--bm-text)]'}`}
                                data-testid={`gl-tab-${t.key}`}>
                                {t.label}
                            </button>
                        ))}
                    </div>
                    {['trial', 'pl', 'vat'].includes(tab) && (
                        <div className="flex items-end gap-3">
                            <div>
                                <Label className="text-[11px] text-[color:var(--bm-text-muted)]">From</Label>
                                <Input type="date" value={start} onChange={e => setStart(e.target.value)} className="h-9 w-[150px]" />
                            </div>
                            <div>
                                <Label className="text-[11px] text-[color:var(--bm-text-muted)]">To</Label>
                                <Input type="date" value={end} onChange={e => setEnd(e.target.value)} className="h-9 w-[150px]" />
                            </div>
                        </div>
                    )}
                </div>

                {tab === 'trial'    && <TrialBalance start={start} end={end} />}
                {tab === 'pl'       && <ProfitLoss   start={start} end={end} />}
                {tab === 'bs'       && <BalanceSheet end={end} />}
                {tab === 'vat'      && <VATReturn    start={start} end={end} />}
                {tab === 'journals' && <Journals />}
                {tab === 'coa'      && <ChartOfAccounts />}
            </div>
        </div>
    );
}


function TrialBalance({ start, end }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get('/gl/trial-balance', { params: { start, end } })
            .then(r => setData(r.data))
            .catch(e => toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'));
    }, [start, end]);
    if (!data) return <div className="text-[color:var(--bm-text-muted)]">Loading…</div>;

    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-x-auto">
            <table className="w-full text-[12.5px]">
                <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                    <tr>
                        <th className="text-left font-medium px-4 py-3">Code</th>
                        <th className="text-left font-medium px-4 py-3">Account</th>
                        <th className="text-left font-medium px-3 py-3">Type</th>
                        <th className="text-right font-medium px-3 py-3">Debit</th>
                        <th className="text-right font-medium px-3 py-3">Credit</th>
                        <th className="text-right font-medium px-3 py-3">Balance</th>
                    </tr>
                </thead>
                <tbody>
                    {data.rows.length === 0 && (
                        <tr><td colSpan={6} className="px-4 py-12 text-center text-[color:var(--bm-text-muted)]">No journal entries in this period yet.</td></tr>
                    )}
                    {data.rows.map(r => (
                        <tr key={r.account_code} className="border-t border-[color:var(--bm-border)]">
                            <td className="px-4 py-2.5 font-mono text-[11.5px]">{r.account_code}</td>
                            <td className="px-4 py-2.5 font-medium">{r.account_name}</td>
                            <td className="px-3 py-2.5 capitalize text-[color:var(--bm-text-muted)]">{r.account_type}</td>
                            <td className="px-3 py-2.5 text-right">{r.debit > 0 ? fmt(r.debit) : ''}</td>
                            <td className="px-3 py-2.5 text-right">{r.credit > 0 ? fmt(r.credit) : ''}</td>
                            <td className={`px-3 py-2.5 text-right font-semibold ${r.balance < 0 ? 'text-red-600' : ''}`}>{fmt(r.balance)}</td>
                        </tr>
                    ))}
                    <tr className="border-t-2 border-[color:var(--bm-border)] bg-[color:var(--bm-bg-mute)] font-semibold">
                        <td colSpan={3} className="px-4 py-3">Totals</td>
                        <td className="px-3 py-3 text-right">{fmt(data.totals.debit)}</td>
                        <td className="px-3 py-3 text-right">{fmt(data.totals.credit)}</td>
                        <td className="px-3 py-3 text-right">{Math.abs(data.totals.debit - data.totals.credit) < 0.01 ? '✓ balanced' : '⚠ unbalanced'}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}


function ProfitLoss({ start, end }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get('/gl/profit-loss', { params: { start, end } }).then(r => setData(r.data)).catch(() => {});
    }, [start, end]);
    if (!data) return <div className="text-[color:var(--bm-text-muted)]">Loading…</div>;

    const Section = ({ title, rows, total, totalLabel, color }) => (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)]">
            <div className="px-5 py-3 border-b border-[color:var(--bm-border)] font-display text-[14px] font-semibold">{title}</div>
            <table className="w-full text-[12.5px]">
                <tbody>
                    {rows.length === 0 && <tr><td className="px-5 py-3 text-[color:var(--bm-text-muted)]">No activity</td></tr>}
                    {rows.map(r => (
                        <tr key={r.account_code} className="border-t border-[color:var(--bm-border)]/60">
                            <td className="px-5 py-2 font-mono text-[11.5px] text-[color:var(--bm-text-muted)]">{r.account_code}</td>
                            <td className="px-3 py-2">{r.account_name}</td>
                            <td className="px-5 py-2 text-right">{fmt(r.amount)}</td>
                        </tr>
                    ))}
                    <tr className="border-t-2 border-[color:var(--bm-border)] bg-[color:var(--bm-bg-mute)] font-semibold">
                        <td colSpan={2} className="px-5 py-2.5">{totalLabel}</td>
                        <td className={`px-5 py-2.5 text-right ${color}`}>{fmt(total)}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="space-y-4">
            <Section title="Income"   rows={data.income}   total={data.income_total}   totalLabel="Total income"   color="text-emerald-700" />
            <Section title="Expenses" rows={data.expenses} total={data.expense_total} totalLabel="Total expenses" color="text-red-700" />
            <div className="bg-[color:var(--bm-primary)]/5 border-2 border-[color:var(--bm-primary)] rounded-xl p-5 flex justify-between items-center">
                <div className="font-display text-[16px] font-semibold">Net profit / (loss)</div>
                <div className={`font-display text-[24px] font-bold ${data.net_profit < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{fmt(data.net_profit)}</div>
            </div>
        </div>
    );
}


function BalanceSheet({ end }) {
    const [data, setData] = useState(null);
    useEffect(() => { api.get('/gl/balance-sheet', { params: { end } }).then(r => setData(r.data)).catch(() => {}); }, [end]);
    if (!data) return <div className="text-[color:var(--bm-text-muted)]">Loading…</div>;
    const Block = ({ title, rows, total }) => (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)]">
            <div className="px-5 py-3 border-b border-[color:var(--bm-border)] font-display text-[14px] font-semibold">{title}</div>
            <table className="w-full text-[12.5px]">
                <tbody>
                    {rows.length === 0 && <tr><td className="px-5 py-3 text-[color:var(--bm-text-muted)]">—</td></tr>}
                    {rows.map(r => (
                        <tr key={r.account_code + r.account_name} className="border-t border-[color:var(--bm-border)]/60">
                            <td className="px-5 py-2 font-mono text-[11.5px] text-[color:var(--bm-text-muted)]">{r.account_code}</td>
                            <td className="px-3 py-2">{r.account_name}</td>
                            <td className="px-5 py-2 text-right">{fmt(r.amount)}</td>
                        </tr>
                    ))}
                    <tr className="border-t-2 border-[color:var(--bm-border)] bg-[color:var(--bm-bg-mute)] font-semibold">
                        <td colSpan={2} className="px-5 py-2.5">Total {title.toLowerCase()}</td>
                        <td className="px-5 py-2.5 text-right">{fmt(total)}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
    return (
        <div className="grid lg:grid-cols-2 gap-4">
            <Block title="Assets"      rows={data.assets}      total={data.assets_total} />
            <div className="space-y-4">
                <Block title="Liabilities" rows={data.liabilities} total={data.liabilities_total} />
                <Block title="Equity"      rows={data.equity}      total={data.equity_total} />
                <div className={`rounded-xl p-4 text-center ${data.balanced ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                    {data.balanced ? '✓ Books balance' : '⚠ Books do not balance'} — Assets {fmt(data.assets_total)} = L+E {fmt(data.liabilities_total + data.equity_total)}
                </div>
            </div>
        </div>
    );
}


function VATReturn({ start, end }) {
    const [data, setData] = useState(null);
    useEffect(() => { api.get('/gl/vat-return', { params: { start, end } }).then(r => setData(r.data)).catch(() => {}); }, [start, end]);
    if (!data) return <div className="text-[color:var(--bm-text-muted)]">Loading…</div>;
    return (
        <div className="space-y-3">
            <div className="bg-white rounded-xl border border-[color:var(--bm-border)] divide-y divide-[color:var(--bm-border)]">
                {Object.entries(data.boxes).map(([num, b]) => (
                    <div key={num} className="flex items-center justify-between px-5 py-3">
                        <div>
                            <div className="text-[11px] text-[color:var(--bm-text-subtle)] uppercase tracking-wide font-semibold">Box {num}</div>
                            <div className="text-[13.5px] font-medium">{b.label}</div>
                        </div>
                        <div className={`font-display text-[18px] font-semibold ${num === '5' ? 'text-[color:var(--bm-primary)]' : ''}`}>{fmt(b.value)}</div>
                    </div>
                ))}
            </div>
            <div className="text-[11.5px] text-[color:var(--bm-text-muted)] italic">{data.note}</div>
        </div>
    );
}


function Journals() {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(null);
    const [openLines, setOpenLines] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const load = useCallback(() => {
        api.get('/gl/journals', { params: { limit: 100 } }).then(r => setData(r.data)).catch(() => {});
    }, []);
    useEffect(() => {
        load();
        api.get('/gl/accounts').then(r => setAccounts(r.data.accounts || [])).catch(() => {});
    }, [load]);
    const showJournal = async (j) => {
        setOpen(j);
        setOpenLines(null);
        const r = await api.get(`/gl/journals/${j.id}`);
        setOpenLines(r.data.lines || []);
    };
    const reverse = async (jid) => {
        if (!confirm('Reverse this journal? A new journal will be posted that backs it out.')) return;
        try { await api.post(`/gl/journals/${jid}/reverse`); toast.success('Reversed'); load(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
    };
    return (
        <>
            <div className="flex justify-between items-center">
                <div className="text-[13px] text-[color:var(--bm-text-muted)]">{data?.journals.length ?? 0} journals</div>
                <Button onClick={() => setShowNew(true)} className="h-8" style={{ background: 'var(--bm-primary)' }} data-testid="gl-new-journal">
                    <Plus size={13} className="mr-1" /> New journal
                </Button>
            </div>
            <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-x-auto">
                <table className="w-full text-[12.5px]">
                <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                    <tr>
                        <th className="text-left font-medium px-4 py-3 whitespace-nowrap">Journal #</th>
                        <th className="text-left font-medium px-4 py-3 whitespace-nowrap">Date</th>
                        <th className="text-left font-medium px-4 py-3">Description</th>
                        <th className="text-left font-medium px-3 py-3">Source</th>
                        <th className="text-right font-medium px-3 py-3">Debit</th>
                        <th className="text-right font-medium px-3 py-3">Credit</th>
                        <th className="text-left font-medium px-3 py-3">Status</th>
                        <th className="px-3 py-3"></th>
                    </tr>
                </thead>
                <tbody>
                    {!data && <tr><td colSpan={8} className="px-4 py-10 text-center text-[color:var(--bm-text-muted)]">Loading…</td></tr>}
                    {data && data.journals.length === 0 && (
                        <tr><td colSpan={8} className="px-4 py-12 text-center text-[color:var(--bm-text-muted)]">
                            No journals yet. Click "New journal" for take-on balances, or issue an invoice / categorise a Monzo debit to post automatically.
                        </td></tr>
                    )}
                    {data && data.journals.map(j => (
                        <React.Fragment key={j.id}>
                            <tr onClick={() => showJournal(j)} className="border-t border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)] cursor-pointer">
                                <td className="px-4 py-2.5 font-mono text-[11.5px]">{j.journal_number}</td>
                                <td className="px-4 py-2.5 text-[color:var(--bm-text-muted)] whitespace-nowrap">{j.date}</td>
                                <td className="px-4 py-2.5 max-w-[360px] truncate">{j.description}</td>
                                <td className="px-3 py-2.5 text-[color:var(--bm-text-muted)] capitalize">{(j.source_type || '').replace('_', ' ')}</td>
                                <td className="px-3 py-2.5 text-right">{fmt(j.total_debit)}</td>
                                <td className="px-3 py-2.5 text-right">{fmt(j.total_credit)}</td>
                                <td className={`px-3 py-2.5 capitalize ${j.status === 'reversed' ? 'text-red-600' : ''}`}>{j.status}</td>
                                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                                    {j.status === 'posted' && (
                                        <button onClick={(e) => { e.stopPropagation(); reverse(j.id); }} className="text-[11px] text-red-600 hover:underline">Reverse</button>
                                    )}
                                </td>
                            </tr>
                            {open?.id === j.id && (
                                <tr><td colSpan={8} className="px-5 py-3 bg-indigo-50/40 border-t border-indigo-100">
                                    {!openLines && <div className="text-[12.5px] text-[color:var(--bm-text-muted)]">Loading…</div>}
                                    {openLines && (
                                        <table className="w-full text-[11.5px]">
                                            <thead><tr className="text-[color:var(--bm-text-muted)]"><th className="text-left py-1">Account</th><th className="text-left py-1">Description</th><th className="text-right py-1">Debit</th><th className="text-right py-1">Credit</th></tr></thead>
                                            <tbody>
                                                {openLines.map(l => (
                                                    <tr key={l.id} className="border-t border-indigo-100/60">
                                                        <td className="py-1.5"><span className="font-mono text-[10.5px]">{l.account_code}</span> {l.account_name}</td>
                                                        <td className="py-1.5 text-[color:var(--bm-text-muted)]">{l.description}</td>
                                                        <td className="py-1.5 text-right">{l.debit > 0 ? fmt(l.debit) : ''}</td>
                                                        <td className="py-1.5 text-right">{l.credit > 0 ? fmt(l.credit) : ''}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </td></tr>
                            )}
                        </React.Fragment>
                    ))}
                </tbody>
                </table>
            </div>
            {showNew && <NewJournalDialog accounts={accounts} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
        </>
    );
}


function NewJournalDialog({ accounts, onClose, onSaved }) {
    const today = new Date().toISOString().slice(0, 10);
    const [date, setDate] = useState(today);
    const [desc, setDesc] = useState('');
    const [lines, setLines] = useState([
        { account_code: '', debit: '', credit: '', description: '' },
        { account_code: '', debit: '', credit: '', description: '' },
    ]);
    const [saving, setSaving] = useState(false);

    const totalDr = lines.reduce((s, l) => s + (parseFloat(l.debit)  || 0), 0);
    const totalCr = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
    const balanced = Math.abs(totalDr - totalCr) < 0.01 && totalDr > 0;

    const updLine = (idx, field, value) => setLines(arr => arr.map((l, i) => i === idx ? { ...l, [field]: value } : l));
    const addLine = () => setLines(arr => [...arr, { account_code: '', debit: '', credit: '', description: '' }]);
    const delLine = (idx) => setLines(arr => arr.length > 2 ? arr.filter((_, i) => i !== idx) : arr);

    const submit = async () => {
        if (!desc) return toast.error('Add a description');
        if (!balanced) return toast.error(`Journal must balance — DR ${totalDr.toFixed(2)} ≠ CR ${totalCr.toFixed(2)}`);
        if (lines.some(l => !l.account_code)) return toast.error('Pick an account for every line');
        setSaving(true);
        try {
            const payload = {
                date,
                description: desc,
                source_type: 'manual',
                lines: lines.map(l => ({
                    account_code: l.account_code,
                    debit:        parseFloat(l.debit)  || 0,
                    credit:       parseFloat(l.credit) || 0,
                    description:  l.description || '',
                })),
            };
            await api.post('/gl/journals', payload);
            toast.success('Journal posted');
            onSaved();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Post failed'); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-3xl" data-testid="gl-new-journal-dialog">
                <DialogHeader><DialogTitle>New manual journal</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label className="text-[12px]">Date</Label>
                            <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="mt-1.5 h-10" />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-[12px]">Description</Label>
                            <Input value={desc} onChange={e => setDesc(e.target.value)} className="mt-1.5 h-10" placeholder="e.g. Take-on balance — opening A/R" data-testid="gl-jrnl-desc" />
                        </div>
                    </div>

                    <div className="bg-[color:var(--bm-bg-mute)] rounded-lg border border-[color:var(--bm-border)] p-3">
                        <div className="grid grid-cols-12 gap-2 text-[11px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold mb-2">
                            <div className="col-span-4">Account</div>
                            <div className="col-span-4">Description</div>
                            <div className="col-span-1 text-right">Debit</div>
                            <div className="col-span-2 text-right">Credit</div>
                            <div className="col-span-1"></div>
                        </div>
                        {lines.map((l, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-2 mb-1.5 items-center">
                                <div className="col-span-4">
                                    <Select value={l.account_code} onValueChange={v => updLine(idx, 'account_code', v)}>
                                        <SelectTrigger className="h-9 bg-white"><SelectValue placeholder="Pick account" /></SelectTrigger>
                                        <SelectContent>
                                            {accounts.filter(a => !a.deleted_at).map(a => (
                                                <SelectItem key={a.code} value={a.code}>
                                                    <span className="font-mono text-[11px] mr-2">{a.code}</span> {a.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="col-span-4">
                                    <Input value={l.description} onChange={e => updLine(idx, 'description', e.target.value)} className="h-9 bg-white" placeholder="Optional" />
                                </div>
                                <div className="col-span-1">
                                    <Input type="number" step="0.01" value={l.debit} onChange={e => updLine(idx, 'debit', e.target.value)} className="h-9 bg-white text-right" />
                                </div>
                                <div className="col-span-2">
                                    <Input type="number" step="0.01" value={l.credit} onChange={e => updLine(idx, 'credit', e.target.value)} className="h-9 bg-white text-right" />
                                </div>
                                <div className="col-span-1 text-right">
                                    <button onClick={() => delLine(idx)} className="text-red-600 hover:text-red-700 disabled:opacity-30" disabled={lines.length <= 2}>
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button onClick={addLine} className="text-[12px] text-[color:var(--bm-primary)] hover:underline mt-1">+ Add line</button>
                    </div>

                    <div className={`rounded-lg p-3 text-[13px] flex justify-between font-medium ${balanced ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
                        <div>Total DR: {fmt(totalDr)}</div>
                        <div>Total CR: {fmt(totalCr)}</div>
                        <div>{balanced ? '✓ Balanced' : `Difference ${fmt(totalDr - totalCr)}`}</div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving || !balanced} style={{ background: 'var(--bm-primary)' }} data-testid="gl-jrnl-save">
                        {saving ? 'Posting…' : 'Post journal'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function ChartOfAccounts() {
    const [accounts, setAccounts] = useState([]);
    const load = () => api.get('/gl/accounts').then(r => setAccounts(r.data.accounts || [])).catch(() => {});
    useEffect(load, []);
    const reseed = async () => {
        if (!confirm('Re-seed missing default accounts based on your organisation type? Existing accounts are preserved.')) return;
        try { await api.post('/gl/_bootstrap'); toast.success('Chart of accounts updated'); load(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
    };
    return (
        <>
            <div className="flex justify-between items-center">
                <div className="text-[13px] text-[color:var(--bm-text-muted)]">{accounts.length} accounts</div>
                <Button size="sm" variant="outline" className="h-8" onClick={reseed} data-testid="gl-reseed">Re-seed defaults</Button>
            </div>
            <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-x-auto">
                <table className="w-full text-[12.5px]">
                    <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                        <tr>
                            <th className="text-left font-medium px-4 py-3">Code</th>
                            <th className="text-left font-medium px-4 py-3">Name</th>
                            <th className="text-left font-medium px-3 py-3">Type</th>
                            <th className="text-left font-medium px-3 py-3">Sub-type</th>
                        </tr>
                    </thead>
                    <tbody>
                        {accounts.map(a => (
                            <tr key={a.id} className="border-t border-[color:var(--bm-border)]">
                                <td className="px-4 py-2 font-mono text-[11.5px]">{a.code}</td>
                                <td className="px-4 py-2 font-medium">
                                    {a.name}
                                    {a.is_vat_control && <span className="ml-2 text-[10.5px] bm-pill bg-amber-50 border border-amber-200 text-amber-700">VAT control</span>}
                                    {a.is_bank && <span className="ml-2 text-[10.5px] bm-pill bg-emerald-50 border border-emerald-200 text-emerald-700">Bank</span>}
                                </td>
                                <td className="px-3 py-2 capitalize text-[color:var(--bm-text-muted)]">{a.type}</td>
                                <td className="px-3 py-2 text-[color:var(--bm-text-muted)]">{a.sub_type || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}
